import { memo, useEffect, useState } from "react"
import { useSelector } from "react-redux"
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { LoadingButton } from "@mui/lab"
import { usePostCartJsActionMutation } from "src/features/checkout/cartJsApi"
import { useLazyGetCustomerOrderLimitsQuery } from "src/features/checkout/checkoutApi"
import { useReplaceLineItemsMutation } from "src/features/checkout/storefrontApi"

function OrderLimitsModal() {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"))
  const { cart, customer, checkout } = useSelector((state) => state.checkout)
  const [orderLimitsViolations, setOrderLimitsViolations] = useState([])

  const [getCustomerOrderLimits, { data: customerOrderLimits }] =
    useLazyGetCustomerOrderLimitsQuery()

  const [postCartAction, { isLoading: postCartActionLoading }] =
    usePostCartJsActionMutation()

  const [replaceLineItems, { isLoading: replaceLineItemsLoading }] =
    useReplaceLineItemsMutation()

  async function adjustQuantities() {
    const lineItems = cart.lines.edges.map((lineItem) => {
      let newQuantity = lineItem.node.quantity
      const lineItemQuantity = lineItem.node.quantity
      const violation = orderLimitsViolations.find(
        (violation) => violation.lineItem.id === lineItem.node.id,
      )

      // Check if lineItem has order limits violation.
      if (violation) {
        const maxQuantity = violation.orderLimit.max_quantity
        const minQuantity = violation.orderLimit.min_quantity
        const purchasedQuantity =
          violation.customerOrderLimit?.purchased_quantity || 0
        let remainingQuantity = maxQuantity - purchasedQuantity

        if (remainingQuantity < 0) remainingQuantity = 0

        if (lineItemQuantity >= maxQuantity) {
          newQuantity = remainingQuantity
        } else if (lineItemQuantity < minQuantity) {
          newQuantity = minQuantity
        }
      }

      return {
        variantId: lineItem.node.merchandise.id,
        quantity: newQuantity,
      }
    })

    const cartLineItems = {}

    lineItems.forEach((lineItem) => {
      cartLineItems[
        lineItem.variantId.replace("gid://shopify/ProductVariant/", "")
      ] = lineItem.quantity
    })

    // Update the Shopify cart line items.
    await postCartAction({
      action: "update",
      params: {
        updates: cartLineItems,
      },
    })

    // // Update the Shopify checkout line items.
    // await replaceLineItems({
    //   checkoutId: checkout.id,
    //   lineItems: lineItems.filter((lineItem) => lineItem.quantity > 0),
    // })

    window.location.reload()
  }

  useEffect(() => {
    if (customer.email || cart.buyerIdentity?.email) {
      getCustomerOrderLimits({
        email: customer.email || cart.buyerIdentity?.email,
      })
    }
  }, [customer.email, cart.buyerIdentity?.email])

  useEffect(() => {
    if (!cart.lines || !customerOrderLimits) return

    const violations = []
    const now = new Date()
    cart.lines.edges.forEach((lineItem) => {
      const metafield = lineItem.node.merchandise?.product?.metafield
      const lineItemHasOrderLimits =
        metafield?.namespace === "custom" && metafield?.key === "order_limits"

      if (lineItemHasOrderLimits) {
        const orderLimit = JSON.parse(metafield.value)
        const customerOrderLimit =
          customerOrderLimits[lineItem.node.merchandise?.product?.id] ?? null

        if (customerOrderLimit) {
          // There's a customer order limit record.
          const expiresAt = customerOrderLimit?.expires_at
          const purchasedQuantity = customerOrderLimit?.purchased_quantity || 0
          const totalQuantity = lineItem.node.quantity + purchasedQuantity

          // Check if expired.
          if (
            !expiresAt ||
            expiresAt === "INFINITE" ||
            now <= new Date(expiresAt)
          ) {
            // The limit is not yet expired.
            // Include customer order limit data when calculating the limits.
            if (
              lineItem.node.quantity < orderLimit.min_quantity ||
              totalQuantity > orderLimit.max_quantity
            ) {
              violations.push({
                lineItem: lineItem.node,
                orderLimit,
                customerOrderLimit,
              })
            }
          } else {
            // The limit is already expired.
            // Calculate the limit based on order limits set for the product.
            if (
              lineItem.node.quantity < orderLimit.min_quantity ||
              lineItem.node.quantity > orderLimit.max_quantity
            ) {
              violations.push({
                lineItem: lineItem.node,
                orderLimit,
                customerOrderLimit,
              })
            }
          }
        } else {
          // There's no customer order limits record.
          // Calculate the limit based on order limits set for the product.
          if (
            lineItem.node.quantity < orderLimit.min_quantity ||
            lineItem.node.quantity > orderLimit.max_quantity
          ) {
            violations.push({
              lineItem: lineItem.node,
              orderLimit,
              customerOrderLimit,
            })
          }
        }
      }
    })
    if (violations.length) {
      setOrderLimitsViolations(violations)
    }
  }, [cart.lines, customerOrderLimits])

  return (
    <>
      <Dialog
        fullScreen={fullScreen}
        maxWidth="md"
        open={orderLimitsViolations.length > 0}
      >
        <DialogTitle>Limited items in your order</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Some items are subject to limits per customer.
          </Typography>

          <ul>
            {orderLimitsViolations.map((violation, index) => {
              const lineItemTitle = violation.lineItem.merchandise?.product?.title
              const lineItemQuantity = violation.lineItem.quantity
              const minQuantity = violation.orderLimit.min_quantity
              const maxQuantity = violation.orderLimit.max_quantity
              const periodInDays = violation.orderLimit.period_in_days
              const purchasedQuantity =
                violation.customerOrderLimit?.purchased_quantity || 0
              const totalQuantity = lineItemQuantity + purchasedQuantity
              const expiresAt = violation.customerOrderLimit?.expires_at
              const remainingQuantity = maxQuantity - purchasedQuantity
              let limitMessage = ""

              if (remainingQuantity <= 0) {
                limitMessage = "You’ve reached this item’s limit per customer"

                if (expiresAt && expiresAt !== "INFINITE") {
                  limitMessage += `. You can purchase again on ${new Date(
                    expiresAt,
                  ).toLocaleString()}`
                }
              } else {
                if (minQuantity > 1) {
                  limitMessage += `Minimum of ${minQuantity} per order and maximum of ${maxQuantity} per customer`
                } else {
                  limitMessage += `This item is limited to ${maxQuantity} per customer`
                }

                if (periodInDays > 0) {
                  if (periodInDays === 1) {
                    limitMessage += ` per day`
                  } else {
                    limitMessage += ` every ${periodInDays} days`
                  }
                }

                if (purchasedQuantity > 0) {
                  limitMessage += `. You have ${remainingQuantity} remaining`
                }
              }

              return (
                <li key={index}>
                  <Typography color="error" variant="body1" component="span">
                    {lineItemQuantity}
                  </Typography>{" "}
                  &times; {lineItemTitle}:{" "}
                  <Typography color="error" variant="body1" component="span">
                    {limitMessage}.
                  </Typography>
                </li>
              )
            })}
          </ul>

          <Typography variant="body1" gutterBottom>
            Thank you for understanding the limited nature of some of our
            selections.
          </Typography>

          <Typography variant="body1" marginBottom={2}>
            Steps you can take:
          </Typography>

          <Stack
            sx={{
              alignItems: "center",
            }}
          >
            <LoadingButton
              loading={postCartActionLoading || replaceLineItemsLoading}
              variant="contained"
              sx={{
                textAlign: "center",
              }}
              onClick={adjustQuantities}
            >
              Adjust item quantities and continue to checkout
            </LoadingButton>
            <p>OR</p>
            <Button
              variant="contained"
              href="/cart"
              sx={{
                textAlign: "center",
              }}
            >
              Go Back to Cart and remove the items manually
            </Button>
          </Stack>

          <Typography variant="body1" marginTop={2}>
            Should you have any questions or need assistance, please don't
            hesitate to reach out to us. (808) 779-0142 or
            contact@kakaakowine.com
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default memo(OrderLimitsModal)
